import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  CORE_STABILITY_PRINCIPLE,
  assertInCoreScope,
  createBoundary,
  deleteBoundary,
  fetchBoundaryStats,
  listBoundaries,
  type CoreBoundary,
  type ScopeDecision,
} from '@/lib/coreBoundaries';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function BoundariesTab({ org }: Props) {
  const [boundaries, setBoundaries] = useState<CoreBoundary[]>([]);
  const [stats, setStats] = useState({ total: 0, coreFoundations: 0, moduleDomains: 0, areas: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);

  const [checkValue, setCheckValue] = useState('');
  const [decision, setDecision] = useState<ScopeDecision | null>(null);
  const [checking, setChecking] = useState(false);

  const [form, setForm] = useState({ capability: '', area: '', ownership: 'module' as 'core' | 'module', rationale: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([listBoundaries(), fetchBoundaryStats()]);
      setBoundaries(list);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const foundations = boundaries.filter((b) => b.ownership === 'core');
  const moduleDomains = boundaries.filter((b) => b.ownership === 'module');

  const doCheck = async () => {
    if (!checkValue.trim()) return;
    setChecking(true);
    try {
      setDecision(await assertInCoreScope(checkValue.trim()));
    } finally {
      setChecking(false);
    }
  };

  const onAdd = async () => {
    if (!form.capability.trim()) { setError('La capacité est requise.'); return; }
    setSaving(true);
    setError('');
    try {
      await createBoundary({
        capability: form.capability.trim(),
        area: form.area || null,
        ownership: form.ownership,
        description: null,
        rationale: form.rationale || null,
      });
      setForm({ capability: '', area: '', ownership: 'module', rationale: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b: CoreBoundary) => {
    setBusy(b.id);
    try { await deleteBoundary(b.id); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Ne pas mettre tout dans Core.</span> {CORE_STABILITY_PRINCIPLE}
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-list-check-2 text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Capacités classées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-stack-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.coreFoundations}</p>
          <p className="text-xs text-foreground-500">Fondations Core</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-apps-2-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.moduleDomains}</p>
          <p className="text-xs text-foreground-500">Domaines de modules</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-layout-grid-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.areas}</p>
          <p className="text-xs text-foreground-500">Zones fonctionnelles</p>
        </div>
      </div>

      {/* Vérificateur de périmètre */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-shield-keyhole-line text-primary-600"></i>Vérifier le périmètre d’une capacité</h4>
        <div className="flex flex-col sm:flex-row gap-2">
          <input className={INPUT} placeholder="Ex : paie, users, crm_avance…" value={checkValue} onChange={(e) => { setCheckValue(e.target.value); setDecision(null); }} />
          <button onClick={doCheck} disabled={checking} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {checking ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-search-line"></i>} Vérifier
          </button>
        </div>
        {decision && (
          <div className={`mt-3 rounded-md border p-3 text-xs ${decision.ownership === 'core' ? 'bg-primary-50 border-primary-200' : decision.ownership === 'module' ? 'bg-accent-50 border-accent-200' : 'bg-background-100 border-background-200/70'}`}>
            <p className="font-semibold flex items-center gap-1.5 text-foreground-800">
              <i className={decision.ownership === 'core' ? 'ri-stack-line text-primary-600' : decision.ownership === 'module' ? 'ri-apps-2-line text-accent-600' : 'ri-question-line text-foreground-400'}></i>
              {decision.ownership === 'core' ? 'Fondation du Core' : decision.ownership === 'module' ? 'Domaine de module' : 'Capacité extensible'}
              {decision.moduleKey ? <span className="text-foreground-400 font-normal">· module {decision.moduleKey}</span> : null}
            </p>
            <p className="text-foreground-600 mt-1">{decision.reason}</p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Fondations */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-stack-line"></i></span>
              <h4 className="text-sm font-semibold text-foreground-900">Fondations du Core</h4>
              <span className="text-[11px] text-foreground-400 ml-auto">{foundations.length}</span>
            </div>
            <div className="divide-y divide-background-200/60 max-h-[26rem] overflow-y-auto">
              {foundations.map((b) => (
                <div key={b.id} className="flex items-center gap-3 px-4 py-2.5">
                  <i className="ri-checkbox-circle-line text-primary-500"></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground-800">{b.capability}</p>
                    <p className="text-[11px] text-foreground-400 truncate">{b.area} · {b.description}</p>
                  </div>
                  <button onClick={() => remove(b)} disabled={busy === b.id} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
              ))}
            </div>
          </div>

          {/* Domaines de modules */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-apps-2-line"></i></span>
              <h4 className="text-sm font-semibold text-foreground-900">Hors Core (modules)</h4>
              <span className="text-[11px] text-foreground-400 ml-auto">{moduleDomains.length}</span>
            </div>
            <div className="divide-y divide-background-200/60 max-h-[26rem] overflow-y-auto">
              {moduleDomains.map((b) => (
                <div key={b.id} className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-medium text-foreground-800">{b.capability}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">module {b.module_key}</span>
                    <button onClick={() => remove(b)} disabled={busy === b.id} className="ml-auto w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40"><i className="ri-delete-bin-line text-sm"></i></button>
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-0.5">{b.rationale}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-add-line text-accent-600"></i>Déclarer une capacité</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input className={INPUT} placeholder="Capacité (ex : pointage)" value={form.capability} onChange={(e) => setForm({ ...form, capability: e.target.value })} />
          <input className={INPUT} placeholder="Zone (ex : RH)" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
          <select className={INPUT} value={form.ownership} onChange={(e) => setForm({ ...form, ownership: e.target.value as 'core' | 'module' })}>
            <option value="core">Fondation du Core</option>
            <option value="module">Domaine de module</option>
          </select>
        </div>
        <input className={`${INPUT} mt-3`} placeholder="Justification (pourquoi dans le Core ou dans un module)" value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} />
        <button onClick={onAdd} disabled={saving} className="mt-3 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Déclarer
        </button>
      </div>
    </div>
  );
}